import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../../layouts/Layout';
import { apiFetch, downloadPdf } from '../../api';
import * as styles from '../packaging/PackagingSlip.styles';

export default function TestCertificateView() {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [certificate, setCertificate] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  /* =========================
     FETCH CERTIFICATE DATA
     ========================= */
  useEffect(() => {
    if (!id) {
      setError('No certificate ID provided');
      setLoading(false);
      return;
    }

    const fetchCertificate = async () => {
      try {
        const response = await apiFetch(`/api/test-certificate/view/${id}`);
        
        if (!response?.success) {
          setError(response?.message || 'Failed to load test certificate');
          return;
        }

        setCertificate(response.data.certificate);
        setItems(response.data.items || []);
      } catch (err) {
        console.error('Failed to load test certificate:', err);
        setError('Failed to load test certificate: ' + err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchCertificate();
  }, [id]);

  /* =========================
     DOWNLOAD PDF
     ========================= */
  const handleDownloadPdf = async () => {
    if (!id || !certificate?.certificate_no) {
      alert('Cannot download PDF: Certificate data not available');
      return;
    }

    try {
      setDownloadingPdf(true);
      
      const blob = await downloadPdf(`/pdf-test-certificate.php?id=${id}`);
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Test_Certificate_${certificate.certificate_no}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
    } catch (error) {
      console.error('PDF download failed:', error);
      alert(`PDF download failed: ${error.message}`);
    } finally {
      setDownloadingPdf(false);
    }
  };

  /* =========================
     LOADING & ERROR STATES
     ========================= */
  if (loading) {
    return (
      <Layout>
        <div style={styles.pageContainer}>
          <h2>Loading test certificate...</h2>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div style={styles.pageContainer}>
          <div style={{
            padding: '20px',
            backgroundColor: '#fee2e2',
            border: '1px solid #fecaca',
            borderRadius: '8px',
            color: '#991b1b',
            marginBottom: '20px'
          }}>
            <h3>Error</h3>
            <p>{error}</p>
            <button
              onClick={() => navigate('/test-certificate-index')}
              style={{
                padding: '8px 16px',
                backgroundColor: '#dc2626',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                marginTop: '10px'
              }}
            >
              Back to List
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  if (!certificate) {
    return (
      <Layout>
        <div style={styles.pageContainer}>
          <p>Test certificate not found</p>
        </div>
      </Layout>
    );
  }

  /* =========================
     RENDER CERTIFICATE VIEW
     ========================= */
  return (
    <Layout>
      <div style={styles.pageContainer}>
        {/* ================= HEADER ================= */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '30px',
          padding: '20px',
          backgroundColor: 'white',
          borderRadius: '8px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
          border: '1px solid #e5e7eb'
        }}>
          <div>
            <h1 style={{ margin: '0 0 8px 0', fontSize: '24px', fontWeight: '600', color: '#1f2937' }}>
              Test Certificate Details
            </h1>
            <p style={{ margin: '0', fontSize: '16px', color: '#6b7280' }}>
              Certificate No: <strong>{certificate.certificate_no}</strong>
            </p>
          </div>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              onClick={() => navigate('/test-certificate-index')}
              style={{
                padding: '8px 16px',
                backgroundColor: '#f3f4f6',
                color: '#374151',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              ← Back to List
            </button>
            <button
              onClick={handleDownloadPdf}
              disabled={downloadingPdf}
              style={{
                padding: '8px 16px',
                backgroundColor: downloadingPdf ? '#9ca3af' : '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: downloadingPdf ? 'not-allowed' : 'pointer',
                fontSize: '14px'
              }}
            >
              {downloadingPdf ? '⏳ Downloading...' : '📄 Download PDF'}
            </button>
            <span
              style={{
                padding: '8px 16px',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: '600',
                background:
                  certificate.status === 'APPROVED'
                    ? '#d1fae5'
                    : certificate.status === 'REJECTED'
                    ? '#fee2e2'
                    : '#fef3c7',
                color:
                  certificate.status === 'APPROVED'
                    ? '#065f46'
                    : certificate.status === 'REJECTED'
                    ? '#991b1b'
                    : '#92400e',
              }}
            >
              {certificate.status}
            </span>
          </div>
        </div>

        {/* ================= CERTIFICATE DETAILS ================= */}
        <div style={styles.card}>
          <h2 style={{ ...styles.cardTitle, textAlign: 'center', marginBottom: '30px' }}>
            TEST CERTIFICATE
          </h2>

          <div style={styles.formGrid}>
            {/* Row 1 */}
            <div style={styles.formGroup}>
              <label style={styles.labelStyle}>
                <strong>Test Certificate No.</strong>
              </label>
              <div style={{
                ...styles.inputStyle,
                backgroundColor: '#f9fafb',
                color: '#374151',
                fontWeight: '600'
              }}>
                {certificate.certificate_no}
              </div>
            </div>
            
            <div style={styles.formGroup}>
              <label style={styles.labelStyle}>
                <strong>Date & Time of testing</strong>
              </label>
              <div style={{
                ...styles.inputStyle,
                backgroundColor: '#f9fafb',
                color: '#374151'
              }}>
                {new Date(certificate.test_date.replace(' ', 'T')).toLocaleString("en-GB", {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                  hour12: true
                })}
              </div>
            </div>

            {/* Row 2 - Customer Name */}
            <div style={{ ...styles.formGroup, gridColumn: '1 / span 2' }}>
              <label style={styles.labelStyle}>
                <strong>Customer Name</strong>
              </label>
              <div style={{
                ...styles.inputStyle,
                backgroundColor: '#f9fafb',
                color: '#374151',
                fontWeight: '600'
              }}>
                {certificate.customer_name}
              </div>
            </div>

            {/* Row 3 */}
            <div style={styles.formGroup}>
              <label style={styles.labelStyle}>
                <strong>Customer's PO No.</strong>
              </label>
              <div style={{ display: 'flex', gap: '10px' }}>
                <div style={{
                  ...styles.inputStyle,
                  backgroundColor: '#f9fafb',
                  color: '#374151',
                  flex: 1
                }}>
                  {certificate.po_no || '-'}
                </div>
                <div style={{
                  ...styles.inputStyle,
                  backgroundColor: '#f9fafb',
                  color: '#374151',
                  width: '140px'
                }}>
                  {certificate.po_date ? new Date(certificate.po_date).toLocaleDateString("en-GB") : '-'}
                </div>
              </div>
            </div>
            
            <div style={styles.formGroup}>
              <label style={styles.labelStyle}>
                <strong>Our SO No.</strong>
              </label>
              <div style={{ display: 'flex', gap: '10px' }}>
                <div style={{
                  ...styles.inputStyle,
                  backgroundColor: '#f9fafb',
                  color: '#374151',
                  flex: 1
                }}>
                  {certificate.so_no || '-'}
                </div>
                <div style={{
                  ...styles.inputStyle,
                  backgroundColor: '#f9fafb',
                  color: '#374151',
                  width: '140px'
                }}>
                  {certificate.so_date ? new Date(certificate.so_date).toLocaleDateString("en-GB") : '-'}
                </div>
              </div>
            </div>

            {/* Divider */}
            <div style={{ 
              gridColumn: '1 / span 2',
              height: '1px',
              backgroundColor: '#e5e7eb',
              margin: '20px 0'
            }} />

            {/* Components supplied as */}
            <div style={{ ...styles.formGroup, gridColumn: '1 / span 2' }}>
              <label style={{ ...styles.labelStyle, marginBottom: '10px' }}>
                <strong>Components are supplied as :</strong>
              </label>
              
              <div style={{ display: 'flex', gap: '30px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <input
                    type="radio"
                    name="supply_as_view"
                    checked={certificate.supply_as === 'gas_train'}
                    readOnly
                    disabled
                  />
                  <span style={{ color: certificate.supply_as === 'gas_train' ? '#374151' : '#9ca3af' }}>
                    A part of Gas train
                  </span>
                </label>
                
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <input
                    type="radio"
                    name="supply_as_view"
                    checked={certificate.supply_as === 'individual'}
                    readOnly
                    disabled
                  />
                  <span style={{ color: certificate.supply_as === 'individual' ? '#374151' : '#9ca3af' }}>
                    Individual components
                  </span>
                </label>
              </div>
            </div>

            {/* Gas Train Equipment No - Only show if gas train is selected */}
            {certificate.supply_as === 'gas_train' && (
              <div style={{ ...styles.formGroup, gridColumn: '1 / span 2' }}>
                <label style={styles.labelStyle}>
                  <strong>Gas Train Equipment No</strong>
                </label>
                <div style={{
                  ...styles.inputStyle,
                  backgroundColor: '#f9fafb',
                  color: '#374151',
                  fontWeight: '600'
                }}>
                  {certificate.gas_train_no || '-'}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ================= ITEMS TABLE ================= */}
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <h3 style={styles.cardTitle}>
              📦 Tested Items
              <span style={styles.itemCountBadge}>
                {items.length}
              </span>
            </h3>
          </div>

          <div style={styles.tableContainer}>
            <table style={styles.tableStyle}>
              <thead>
                <tr>
                  <th style={styles.thStyle}>Sr. No</th>
                  <th style={styles.thStyle}>Item Code</th>
                  <th style={styles.thStyle}>Item Description</th>
                  <th style={styles.thStyle}>Qty</th>
                  <th style={styles.thStyle}>Lot Code / Serial Nos.</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item, idx) => (
                  <tr key={idx} style={styles.trStyle(false)}>
                    <td style={styles.tdStyle}><b>{idx + 1}</b></td>
                    <td style={styles.tdStyle}><b>{item.item_code}</b></td>
                    <td style={styles.tdStyle}>
                      <div>{item.item_description}</div>
                      {item.test_type && (
                        <div style={{
                          fontSize: '12px',
                          color: '#6b7280',
                          fontStyle: 'italic',
                          marginTop: '4px'
                        }}>
                          ({item.test_type})
                        </div>
                      )}
                    </td>
                    <td style={styles.tdStyle}>{item.qty}</td>
                    <td style={styles.tdStyle}>
                      {item.lot_code || <i style={{ color: '#9ca3af' }}>No lot code</i>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {items.length === 0 && (
              <div style={styles.emptyState}>
                <div style={{ fontSize: 40 }}>📦</div>
                <p>No items found in this certificate</p>
              </div>
            )}
          </div>
        </div>

        {/* ================= SUMMARY ================= */}
        <div style={{
          ...styles.card,
          backgroundColor: '#f8fafc',
          border: '1px solid #e2e8f0'
        }}>
          <h3 style={{ ...styles.cardTitle, marginBottom: '20px' }}>
            📊 Summary
          </h3>
          
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '20px'
          }}>
            <div style={{
              padding: '16px',
              backgroundColor: 'white',
              borderRadius: '8px',
              border: '1px solid #e5e7eb'
            }}>
              <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '4px' }}>
                Total Items
              </div>
              <div style={{ fontSize: '24px', fontWeight: '600', color: '#1f2937' }}>
                {certificate.total_items || 0}
              </div>
            </div>
            
            <div style={{
              padding: '16px',
              backgroundColor: 'white',
              borderRadius: '8px',
              border: '1px solid #e5e7eb'
            }}>
              <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '4px' }}>
                Total Quantity
              </div>
              <div style={{ fontSize: '24px', fontWeight: '600', color: '#1f2937' }}>
                {certificate.total_qty || 0}
              </div>
            </div>
            
            <div style={{
              padding: '16px',
              backgroundColor: 'white',
              borderRadius: '8px',
              border: '1px solid #e5e7eb'
            }}>
              <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '4px' }}>
                Created Date
              </div>
              <div style={{ fontSize: '16px', fontWeight: '600', color: '#1f2937' }}>
                {new Date(certificate.created_at).toLocaleDateString("en-GB")}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}